/**
 * Utility functions for validating prompt variables in string interpolation.
 * Checks that all {variable_name} patterns in prompts correspond to valid dataset fields.
 */

/**
 * Extracts all variable names from a prompt string that uses {variable_name} interpolation.
 *
 * @param prompt - The prompt string to analyze
 * @returns Array of variable names found in the prompt
 *
 * @example
 * ```typescript
 * extractVariableNames("Rate this response: {input_text} vs {output}")
 * // Returns: ["input_text", "output"]
 * ```
 */
export function extractVariableNames(prompt: string): string[] {
  if (!prompt || typeof prompt !== 'string') {
    return [];
  }

  // Match {variable_name} patterns, allowing for nested braces and escaping
  const variablePattern = /\{([^}]+)\}/g;
  const matches = prompt.match(variablePattern);

  if (!matches) {
    return [];
  }

  // Extract the variable names (remove the braces)
  return matches.map(match => match.slice(1, -1));
}

/**
 * Validates that all variable names in a prompt are present in the allowed fields.
 *
 * @param prompt - The prompt string to validate
 * @param allowedFields - Array of field names that are valid for interpolation
 * @returns Object containing validation result and any invalid variables found
 *
 * @example
 * ```typescript
 * const result = validatePromptVariables(
 *   "Rate this: {input_text} vs {output}",
 *   ["input_text", "output", "score"]
 * );
 * // Returns: { isValid: true, invalidVariables: [] }
 *
 * const result2 = validatePromptVariables(
 *   "Rate this: {input_text} vs {invalid_field}",
 *   ["input_text", "output"]
 * );
 * // Returns: { isValid: false, invalidVariables: ["invalid_field"] }
 * ```
 */
export function validatePromptVariables(
  prompt: string,
  allowedFields: string[]
): {
  isValid: boolean;
  invalidVariables: string[];
} {
  const variableNames = extractVariableNames(prompt);
  const allowedFieldsSet = new Set(allowedFields);

  const invalidVariables = variableNames.filter(
    variable => !allowedFieldsSet.has(variable)
  );

  return {
    isValid: invalidVariables.length === 0,
    invalidVariables,
  };
}

/**
 * Gets the available fields for prompt interpolation based on dataset columns and model output.
 *
 * @param datasetColumns - Array of column names from the dataset
 * @returns Array of all available fields for interpolation
 *
 * @example
 * ```typescript
 * const availableFields = getAvailableFields(["input_text", "reference", "id"]);
 * // Returns: ["input_text", "reference", "id", "output"]
 * ```
 */
export function getAvailableFields(datasetColumns: string[]): string[] {
  // Always include 'output' for model output
  const baseFields = ['output'];

  // Add dataset columns
  return [...baseFields, ...datasetColumns];
}

/**
 * Creates a user-friendly error message for invalid prompt variables.
 *
 * @param invalidVariables - Array of invalid variable names
 * @param availableFields - Array of available field names
 * @returns Formatted error message
 *
 * @example
 * ```typescript
 * const message = createValidationErrorMessage(
 *   ["invalid_field"],
 *   ["input_text", "output"]
 * );
 * // Returns: "Invalid variable 'invalid_field'. Available variables: input_text, output"
 * ```
 */
export function createValidationErrorMessage(
  invalidVariables: string[],
  availableFields: string[]
): string {
  if (invalidVariables.length === 0) {
    return '';
  }

  const invalidList = invalidVariables.map(v => `'${v}'`).join(', ');
  const availableList = availableFields.join(', ');

  if (invalidVariables.length === 1) {
    return `Invalid variable ${invalidList}. Available variables: ${availableList}`;
  }

  return `Invalid variables: ${invalidList}. Available variables: ${availableList}`;
}
